/**
 * Chilean RUT validation and formatting utilities
 * Based on backend/src/utils/rut_validator.py
 */

export interface RutValidationResult {
  valid: boolean;
  cleanRut?: string;
  error?: string;
}

/**
 * Remove dots and hyphen from RUT, return uppercase string
 * Example: '76.543.210-K' → '76543210K'
 */
export const cleanRut = (rut: string): string => {
  return rut.replace(/\./g, '').replace(/-/g, '').toUpperCase();
};

/**
 * Calculate the check digit using the modulo 11 algorithm
 * Multiplies each digit of the body (right to left) by the cyclic series 2,3,4,5,6,7
 */
export const calculateCheckDigit = (body: string): string => {
  const multipliers = [2, 3, 4, 5, 6, 7];
  let total = 0;
  
  const reversedBody = body.split('').reverse();
  for (let i = 0; i < reversedBody.length; i++) {
    total += parseInt(reversedBody[i]) * multipliers[i % multipliers.length];
  }
  
  const remainder = 11 - (total % 11);
  if (remainder === 11) return '0';
  if (remainder === 10) return 'K';
  return remainder.toString();
};

/**
 * Validate RUT format and check digit
 * Returns validation result with clean RUT if valid
 */
export const validateRut = (rut: string): RutValidationResult => {
  const cleaned = cleanRut(rut);
  
  if (cleaned.length < 2) {
    return {
      valid: false,
      error: 'El RUT ingresado tiene un formato inválido'
    };
  }
  
  const body = cleaned.slice(0, -1);
  const providedDv = cleaned.slice(-1);
  
  if (!/^\d+$/.test(body)) {
    return {
      valid: false,
      error: 'El RUT ingresado tiene un formato inválido'
    };
  }
  
  const expectedDv = calculateCheckDigit(body);
  
  if (providedDv !== expectedDv) {
    return {
      valid: false,
      error: 'El dígito verificador del RUT es incorrecto'
    };
  }
  
  return {
    valid: true,
    cleanRut: cleaned
  };
};

/**
 * Format a clean RUT to dotted-hyphen format
 * Example: '76543210K' → '76.543.210-K'
 */
export const formatRut = (rutLimpio: string): string => {
  const cleaned = rutLimpio.toUpperCase();
  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1);
  
  // Insert dots every 3 digits from right
  let formattedBody = '';
  const reversedBody = body.split('').reverse();
  
  for (let i = 0; i < reversedBody.length; i++) {
    if (i > 0 && i % 3 === 0) {
      formattedBody = '.' + formattedBody;
    }
    formattedBody = reversedBody[i] + formattedBody;
  }
  
  return `${formattedBody}-${dv}`;
};

/**
 * Get RUT validation error message
 */
export const getRutErrorMessage = (rut: string): string | null => {
  const result = validateRut(rut);
  return result.valid ? null : (result.error || 'RUT inválido');
};
